import { components } from "@/__openapi__/backend/schema";

/**
 * Internal model enum used across the app.
 * Values map to the API's SessionConfigVariant.
 */
export enum ChatModel {
  Auto = "STANDARD",
  Sonnet45 = "CLAUDE_SONNET_4_5",
  Sonnet = "CLAUDE_SONNET_4_6",
  Sonnet5 = "CLAUDE_SONNET_5",
  GPT = "GPT_5_2",
  GPT53 = "GPT_5_3",
  GPT54 = "GPT_5_4",
  GPT56Terra = "GPT_5_6_TERRA",
  Gemini = "GEMINI_3_FLASH",
  Gemini31Pro = "GEMINI_3_1_PRO",
  Gemini35Flash = "GEMINI_3_5_FLASH",
}

export enum ChatModelFamily {
  Auto = "Standard",
  Anthropic = "Anthropic",
  OpenAI = "OpenAI",
  Google = "Google",
}

const CHAT_MODEL_TO_FAMILY: Record<ChatModel, ChatModelFamily> = {
  [ChatModel.Auto]: ChatModelFamily.Auto,
  [ChatModel.Sonnet45]: ChatModelFamily.Anthropic,
  [ChatModel.Sonnet]: ChatModelFamily.Anthropic,
  [ChatModel.Sonnet5]: ChatModelFamily.Anthropic,
  [ChatModel.GPT]: ChatModelFamily.OpenAI,
  [ChatModel.GPT53]: ChatModelFamily.OpenAI,
  [ChatModel.GPT54]: ChatModelFamily.OpenAI,
  [ChatModel.GPT56Terra]: ChatModelFamily.OpenAI,
  [ChatModel.Gemini]: ChatModelFamily.Google,
  [ChatModel.Gemini31Pro]: ChatModelFamily.Google,
  [ChatModel.Gemini35Flash]: ChatModelFamily.Google,
};

export function getChatModelFamily(model: ChatModel): ChatModelFamily {
  return CHAT_MODEL_TO_FAMILY[model];
}

export type ApiWebClipModelKind = components["schemas"]["WebClipIntegrationModelKind"];

/**
 * Includes branch-only backend variants until `script/update` can generate them
 * from the deployed production schema.
 */
export type WebClipModelKind =
  | ApiWebClipModelKind
  | "CLAUDE_SONNET_5"
  | "GPT_5_6_TERRA"
  | "GEMINI_3_5_FLASH";

const CHAT_MODEL_TO_WEB_CLIP: Record<ChatModel, WebClipModelKind> = {
  [ChatModel.Auto]: "AUTO",
  [ChatModel.Sonnet45]: "CLAUDE_SONNET_4_5",
  [ChatModel.Sonnet]: "CLAUDE_SONNET_4_6",
  [ChatModel.Sonnet5]: "CLAUDE_SONNET_5",
  [ChatModel.GPT]: "GPT_5_2",
  [ChatModel.GPT53]: "GPT_5_3",
  [ChatModel.GPT54]: "GPT_5_4",
  [ChatModel.GPT56Terra]: "GPT_5_6_TERRA",
  [ChatModel.Gemini]: "GEMINI_3_FLASH",
  [ChatModel.Gemini31Pro]: "GEMINI_3_1_PRO",
  [ChatModel.Gemini35Flash]: "GEMINI_3_5_FLASH",
};

const WEB_CLIP_TO_CHAT_MODEL: Record<string, ChatModel> = {
  AUTO: ChatModel.Auto,
  CLAUDE_SONNET_4_5: ChatModel.Sonnet45,
  CLAUDE_SONNET_4_6: ChatModel.Sonnet,
  CLAUDE_SONNET_5: ChatModel.Sonnet5,
  GPT_5_2: ChatModel.GPT,
  GPT_5_3: ChatModel.GPT53,
  GPT_5_4: ChatModel.GPT54,
  GPT_5_6_TERRA: ChatModel.GPT56Terra,
  GEMINI_3_FLASH: ChatModel.Gemini,
  GEMINI_3_1_PRO: ChatModel.Gemini31Pro,
  GEMINI_3_5_FLASH: ChatModel.Gemini35Flash,
};

export function chatModelToWebClip(model: ChatModel): WebClipModelKind {
  return CHAT_MODEL_TO_WEB_CLIP[model];
}

export function webClipToChatModel(kind: string | null | undefined): ChatModel {
  if (!kind) return ChatModel.Auto;
  return WEB_CLIP_TO_CHAT_MODEL[kind] ?? ChatModel.Auto;
}
